import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Megaphone, Globe, TrendingUp, TrendingDown, Building2, Calendar, Euro, Check, X, Eye, Heart, Users, Mail } from "lucide-react";

const STATUS_FLOW = ["draft", "submitted", "approved", "active", "completed", "rejected"];
const STATUS_META = {
  draft: { label: "Bozza", cls: "bg-muted text-muted-foreground border" },
  submitted: { label: "Inviata", cls: "bg-blue-500/15 text-blue-500 border-blue-500/30" },
  approved: { label: "Approvata", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  active: { label: "In corso", cls: "bg-[#C9A961]/15 text-[#C9A961] border-[#C9A961]/40" },
  completed: { label: "Conclusa", cls: "bg-purple-500/15 text-purple-500 border-purple-500/30" },
  rejected: { label: "Rifiutata", cls: "bg-red-500/15 text-red-500 border-red-500/30" },
};
const fmtEur = (n) => `€${Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

function ReviewModal({ campaign, onClose, onSaved }) {
  const [status, setStatus] = useState(campaign.status);
  const [notes, setNotes] = useState(campaign.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/marketing/campaigns/${campaign.id}/status`, { status, admin_notes: notes });
      toast.success("Aggiornato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="campaign-review-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[hsl(var(--background))] max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-[hsl(var(--border))] rounded-sm">
        <div className="p-6 border-b border-[hsl(var(--border))] flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent))]">{campaign.company_name}</div>
            <h2 className="editorial-heading text-2xl mt-1">{campaign.title}</h2>
            <div className="text-sm text-muted-foreground">Prodotto: {campaign.product_name}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Obiettivo:</span> <strong>{campaign.objective}</strong></div>
            <div><span className="text-muted-foreground">Budget:</span> <strong className="text-[#C9A961]">{fmtEur(campaign.total_budget)}</strong></div>
            <div><span className="text-muted-foreground">Periodo:</span> <strong>{campaign.start_date} → {campaign.end_date}</strong></div>
            <div><span className="text-muted-foreground">Email:</span> <strong>{campaign.user_email}</strong></div>
          </div>

          {campaign.target_audience && (
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Target</div>
              <div className="text-sm bg-[hsl(var(--muted))] p-3 rounded-sm">{campaign.target_audience}</div>
            </div>
          )}

          {campaign.creative_brief && (
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Brief creativo</div>
              <div className="text-sm bg-[hsl(var(--muted))] p-3 rounded-sm whitespace-pre-wrap">{campaign.creative_brief}</div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Canali ({campaign.channels?.length || 0})</div>
            <div className="bg-[hsl(var(--muted))] p-3 rounded-sm space-y-1">
              {(campaign.channels || []).map((ch, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span><span className="uppercase text-[10px] text-[hsl(var(--accent))]">{ch.type}</span> · {ch.channel_id}</span>
                  <span className="font-bold text-[#C9A961]">{fmtEur(ch.budget)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-[hsl(var(--border))] pt-4 space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold">Cambia stato</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="review-status" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_FLOW.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold">Note interne (visibili al rivenditore)</label>
              <Textarea data-testid="review-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="Es. Approvata con riserva di vedere bozza creative entro 7 gg" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="review-save" onClick={save} disabled={saving} className="bg-[hsl(var(--primary))]">{saving ? "Salvo..." : "Salva"}</Button>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CampaignsTab() {
  const [data, setData] = useState({ campaigns: [], total: 0, by_status: {}, total_budget_active: 0 });
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const reload = () => api.get("/admin/marketing/campaigns").then((r) => setData(r.data)).catch(() => toast.error("Errore"));
  useEffect(() => { reload(); }, []);

  const filtered = filter === "all" ? data.campaigns : data.campaigns.filter((c) => c.status === filter);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        <button onClick={() => setFilter("all")} className={`text-left p-3 rounded-sm border transition-colors ${filter === "all" ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-card border-border"}`}>
          <div className="text-[10px] uppercase tracking-wider opacity-70">Totale</div>
          <div className="text-2xl font-bold">{data.total}</div>
        </button>
        {STATUS_FLOW.map((s) => (
          <button key={s} data-testid={`flt-${s}`} onClick={() => setFilter(s)} className={`text-left p-3 rounded-sm border transition-colors ${filter === s ? STATUS_META[s].cls + " border-current" : "bg-card border-border"}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-70">{STATUS_META[s].label}</div>
            <div className="text-2xl font-bold">{data.by_status[s] || 0}</div>
          </button>
        ))}
      </div>

      {data.total_budget_active > 0 && (
        <div className="bg-[hsl(var(--primary))] text-white p-4 rounded-sm flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-80">Budget attivo (approved + active + completed)</div>
            <div className="text-3xl font-bold">{fmtEur(data.total_budget_active)}</div>
          </div>
          <Megaphone className="h-10 w-10 opacity-40" />
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <Megaphone className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <div className="text-lg font-semibold">Nessuna campagna {filter !== "all" ? `in stato "${STATUS_META[filter]?.label}"` : ""}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.draft;
            return (
              <button key={c.id} data-testid={`adm-cmp-${c.id}`} onClick={() => setSelected(c)} className="w-full text-left bg-card border border-border hover:border-[hsl(var(--primary))]/40 rounded-sm p-4 flex items-center justify-between gap-3 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div className="text-sm font-semibold truncate">{c.company_name}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm font-bold ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <div className="text-base font-medium truncate">{c.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-3">
                    <span><Calendar className="inline h-3 w-3" /> {c.start_date} → {c.end_date}</span>
                    <span>{c.channels?.length || 0} canali</span>
                    <span className="text-[#C9A961] font-bold"><Euro className="inline h-3 w-3" />{fmtEur(c.total_budget).replace("€", "")}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && <ReviewModal campaign={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); reload(); }} />}
    </div>
  );
}

function SiteMetricsTab() {
  const [snap, setSnap] = useState(null);

  useEffect(() => {
    api.get("/admin/marketing/site-metrics").then((r) => setSnap(r.data)).catch(() => toast.error("Errore"));
  }, []);

  if (!snap) return <div className="text-muted-foreground py-12 text-center">Caricamento...</div>;

  const channels = snap.channels || {};
  const items = Object.entries(channels).map(([id, m]) => ({ id, ...m }));

  return (
    <div className="space-y-5">
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm p-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent))]">Snapshot periodo</div>
          <div className="text-2xl font-bold mt-1">{snap.period}</div>
        </div>
        <Globe className="h-10 w-10 text-[hsl(var(--accent))] opacity-40" />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => {
          const growthPositive = (c.growth_30d || 0) >= 0;
          const TrendIcon = growthPositive ? TrendingUp : TrendingDown;
          const followers = c.followers ?? c.subscribers ?? null;
          return (
            <div key={c.id} data-testid={`site-channel-${c.id}`} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm p-5 hover:border-[hsl(var(--primary))]/40 transition-colors">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-2">{c.id}</div>
              <h3 className="font-semibold text-base mb-3">{c.label}</h3>

              {followers !== null && (
                <div className="mb-3 pb-3 border-b border-[hsl(var(--border))]">
                  <div className="text-3xl font-bold tabular-nums">{Number(followers).toLocaleString("it-IT")}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{c.subscribers ? "iscritti" : "followers"}</div>
                </div>
              )}

              <div className="space-y-2 text-sm">
                {c.reach_30d !== undefined && (
                  <div className="flex justify-between"><span className="text-muted-foreground"><Eye className="inline h-3 w-3" /> Reach 30g</span> <strong>{Number(c.reach_30d).toLocaleString("it-IT")}</strong></div>
                )}
                {c.views_30d !== undefined && (
                  <div className="flex justify-between"><span className="text-muted-foreground"><Eye className="inline h-3 w-3" /> Views 30g</span> <strong>{Number(c.views_30d).toLocaleString("it-IT")}</strong></div>
                )}
                {c.engagement_rate !== undefined && (
                  <div className="flex justify-between"><span className="text-muted-foreground"><Heart className="inline h-3 w-3" /> Engagement</span> <strong>{c.engagement_rate}%</strong></div>
                )}
                {c.watch_time_hours !== undefined && (
                  <div className="flex justify-between"><span className="text-muted-foreground"><Eye className="inline h-3 w-3" /> Watch time</span> <strong>{Number(c.watch_time_hours).toLocaleString("it-IT")}h</strong></div>
                )}
                {c.open_rate !== undefined && (
                  <div className="flex justify-between"><span className="text-muted-foreground"><Mail className="inline h-3 w-3" /> Open rate</span> <strong>{c.open_rate}%</strong></div>
                )}
                {c.click_rate !== undefined && (
                  <div className="flex justify-between"><span className="text-muted-foreground"><Mail className="inline h-3 w-3" /> Click rate</span> <strong>{c.click_rate}%</strong></div>
                )}
              </div>

              {c.growth_30d !== undefined && (
                <div className={`mt-3 pt-3 border-t border-[hsl(var(--border))] flex items-center gap-1.5 text-xs font-bold ${growthPositive ? "text-emerald-500" : "text-red-500"}`}>
                  <TrendIcon className="h-3.5 w-3.5" />
                  {growthPositive ? "+" : ""}{c.growth_30d}% vs 30g prima
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminMarketing() {
  return (
    <div data-testid="admin-marketing-page" className="container-xl py-8">
      <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent))] mb-2">Super Admin · Marketing</div>
      <h1 className="editorial-heading text-4xl flex items-center gap-3"><Megaphone className="h-8 w-8 text-[hsl(var(--primary))]" />Marketing Hub</h1>
      <p className="text-muted-foreground mt-2 mb-6">Approva i piani marketing dei rivenditori e monitora le performance dei nostri canali social.</p>

      <Tabs defaultValue="campaigns" className="mt-6">
        <TabsList>
          <TabsTrigger data-testid="adm-mk-tab-campaigns" value="campaigns" className="gap-2">
            <Megaphone className="h-4 w-4" /> Piani aziende
          </TabsTrigger>
          <TabsTrigger data-testid="adm-mk-tab-site" value="site" className="gap-2">
            <Globe className="h-4 w-4" /> Sito · Social Performance
          </TabsTrigger>
        </TabsList>
        <TabsContent value="campaigns" className="mt-6"><CampaignsTab /></TabsContent>
        <TabsContent value="site" className="mt-6"><SiteMetricsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
